import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Search, CheckCircle, XCircle, Ban, Eye, Car, Wallet as WalletIcon, KeyRound, Shield } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import WalletAdjustmentModal from '../../components/admin/WalletAdjustmentModal';
import ResetPasswordModal from '../../components/admin/ResetPasswordModal';
import DriverBadgesModal from '../../components/admin/DriverBadgesModal';

const AdminDrivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDriverDetail, setShowDriverDetail] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [showBadgesModal, setShowBadgesModal] = useState(false);
  const [badgesDriver, setBadgesDriver] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchDrivers();
  }, [page, filter]);

  const fetchDrivers = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const params = { skip: page * limit, limit };
      if (filter !== 'all') params.status = filter;

      const response = await api.get('/admin/drivers', { params });
      setDrivers(response.data.drivers || []);
      setTotal(response.data.total || 0);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      toast({
        title: 'Error',
        description: 'Failed to load drivers',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handleViewDetails = async (driverId) => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get(`/admin/drivers/${driverId}`);
      setSelectedDriver(response.data);
      setShowDriverDetail(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load driver details',
        variant: 'destructive',
      });
    }
  };

  const handleApprovalAction = (driver, action) => {
    setSelectedDriver(driver);
    setApprovalAction(action);
    setShowApprovalDialog(true);
  };

  const confirmApprovalAction = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      await api.put(`/admin/drivers/${selectedDriver._id}/approval`, {
        status: approvalAction
      });

      toast({
        title: 'Success',
        description: `Driver ${approvalAction} successfully`,
      });

      setShowApprovalDialog(false);
      fetchDrivers();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update driver status',
        variant: 'destructive',
      });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      suspended: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Drivers</h1>
            <p className="text-gray-600 mt-1">Manage driver applications and accounts</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <Card className="p-4">
          <div className="flex gap-2">
            {['all', 'pending', 'approved', 'rejected', 'suspended'].map((status) => (
              <Button
                key={status}
                variant={filter === status ? 'default' : 'outline'}
                onClick={() => {
                  setFilter(status);
                  setPage(0);
                }}
                className={filter === status ? 'bg-pink-600' : ''}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>
        </Card>

        {/* Drivers Table */}
        <Card className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Driver</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Vehicle</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Wallet</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Rides</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Applied</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((driver) => (
                  <tr key={driver._id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-gray-900">{driver.name}</p>
                        <p className="text-xs text-gray-600">{driver.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {driver.vehicleMake} {driver.vehicleModel}
                        </p>
                        <p className="text-xs text-gray-600">{driver.vehiclePlateNumber}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="space-y-1">
                        <Badge className={getStatusColor(driver.approvalStatus)}>
                          {driver.approvalStatus}
                        </Badge>
                        {driver.isOnline && driver.approvalStatus === 'approved' && (
                          <Badge className="bg-green-100 text-green-800 ml-1">🟢 Online</Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-semibold text-gray-900">K{driver.walletBalance?.toFixed(2)}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-gray-900">{driver.completedRides || 0}</span>
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {new Date(driver.appliedAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(driver._id)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setResetPasswordUser(driver); setShowResetPassword(true); }}
                          className="text-pink-600"
                          data-testid={`reset-password-driver-${driver._id}`}
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        {driver.approvalStatus === 'pending' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleApprovalAction(driver, 'approved')}
                              className="text-green-600"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleApprovalAction(driver, 'rejected')}
                              className="text-red-600"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {driver.approvalStatus === 'approved' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setBadgesDriver(driver); setShowBadgesModal(true); }}
                            className="text-blue-600"
                            title="Manage verification badges"
                            data-testid={`badges-driver-${driver._id}`}
                          >
                            <Shield className="h-4 w-4" />
                          </Button>
                        )}
                        {driver.approvalStatus === 'approved' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleApprovalAction(driver, 'suspended')}
                            className="text-gray-600"
                            title="Suspend"
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                        )}
                        {driver.approvalStatus === 'suspended' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleApprovalAction(driver, 'approved')}
                            className="text-green-600"
                            data-testid={`unsuspend-driver-${driver._id}`}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} drivers
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
              >
                Next
              </Button>
            </div>
          </div>
        </Card>

        {/* Driver Detail Modal */}
        <Dialog open={showDriverDetail} onOpenChange={setShowDriverDetail}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Driver Details</DialogTitle>
            </DialogHeader>
            {selectedDriver && (
              <div className="space-y-4">
                {/* Driver Info */}
                <Card className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Driver Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-medium">{selectedDriver.driver?.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium">{selectedDriver.driver?.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-medium">{selectedDriver.driver?.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <Badge className={getStatusColor(selectedDriver.driver?.approvalStatus)}>
                        {selectedDriver.driver?.approvalStatus}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    onClick={() => setShowWalletModal(true)}
                    className="mt-4 w-full bg-green-600 hover:bg-green-700 flex items-center justify-center gap-2"
                  >
                    <WalletIcon className="h-4 w-4" />
                    Adjust Wallet Balance
                  </Button>
                </Card>

                {/* Vehicle Info */}
                <Card className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Vehicle Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Make & Model</p>
                      <p className="font-medium">
                        {selectedDriver.driver?.vehicleMake} {selectedDriver.driver?.vehicleModel}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Year</p>
                      <p className="font-medium">{selectedDriver.driver?.vehicleYear}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">License Plate</p>
                      <p className="font-medium">{selectedDriver.driver?.vehiclePlateNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Color</p>
                      <p className="font-medium">{selectedDriver.driver?.vehicleColor}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Type</p>
                      <Badge>{selectedDriver.driver?.vehicleType}</Badge>
                    </div>
                  </div>
                </Card>

                {/* Stats */}
                <Card className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Statistics</h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">
                        K{selectedDriver.driver?.walletBalance?.toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-600">Wallet Balance</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">
                        {selectedDriver.driver?.completedRides || 0}
                      </p>
                      <p className="text-sm text-gray-600">Completed</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">
                        {selectedDriver.driver?.rating?.toFixed(1) || '5.0'}
                      </p>
                      <p className="text-sm text-gray-600">Rating</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">
                        K{selectedDriver.driver?.totalCommissionPaid?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-sm text-gray-600">Commission Paid</p>
                    </div>
                  </div>
                </Card>

                {/* Recent Rides */}
                <Card className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Recent Rides</h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedDriver.rides?.slice(0, 10).map((ride, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex justify-between">
                          <div>
                            <p className="font-medium text-sm">{ride.pickup} → {ride.dropoff}</p>
                            <p className="text-xs text-gray-600">
                              {new Date(ride.createdAt).toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">K{ride.price}</p>
                            <Badge className="text-xs">{ride.status}</Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Approval Confirmation Dialog */}
        <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {approvalAction === 'approved' && selectedDriver?.approvalStatus === 'suspended' && 'Unsuspend Driver'}
                {approvalAction === 'approved' && selectedDriver?.approvalStatus !== 'suspended' && 'Approve Driver'}
                {approvalAction === 'rejected' && 'Reject Driver'}
                {approvalAction === 'suspended' && 'Suspend Driver'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="pt-4">
              <p className="text-gray-600 mb-6">
                {approvalAction === 'approved' && selectedDriver?.approvalStatus === 'suspended' && `Are you sure you want to unsuspend ${selectedDriver?.name}? They will be able to accept rides again.`}
                {approvalAction === 'approved' && selectedDriver?.approvalStatus !== 'suspended' && `Are you sure you want to approve ${selectedDriver?.name}? They will be able to start accepting rides.`}
                {approvalAction === 'rejected' && `Are you sure you want to reject ${selectedDriver?.name}'s application?`}
                {approvalAction === 'suspended' && `Are you sure you want to suspend ${selectedDriver?.name}? They will not be able to accept rides.`}
              </p>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowApprovalDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmApprovalAction}
                  className={`flex-1 ${
                    approvalAction === 'approved' ? 'bg-green-600 hover:bg-green-700' :
                    approvalAction === 'rejected' ? 'bg-red-600 hover:bg-red-700' :
                    'bg-gray-600 hover:bg-gray-700'
                  } text-white`}
                >
                  Confirm
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Wallet Adjustment Modal */}
        <WalletAdjustmentModal
          isOpen={showWalletModal}
          onClose={() => setShowWalletModal(false)}
          driver={selectedDriver?.driver}
          onSuccess={() => {
            setShowWalletModal(false);
            fetchDriverDetails(selectedDriver?.driver?.userId);
          }}
        />

        <ResetPasswordModal
          isOpen={showResetPassword}
          onClose={() => setShowResetPassword(false)}
          user={resetPasswordUser}
          userType="driver"
        />

        <DriverBadgesModal
          isOpen={showBadgesModal}
          onClose={() => setShowBadgesModal(false)}
          driver={badgesDriver}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminDrivers;